import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { LogOut, Users, CheckCircle, XCircle, Clock, Truck, Calendar, FileText, Phone, Mail, KeyRound, Briefcase, BookOpen, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboardNew = () => {
  const navigate = useNavigate();
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [allShifts, setAllShifts] = useState({ shifts: [], daily_summary: [] });
  const [fleetRegistrations, setFleetRegistrations] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [activeOtps, setActiveOtps] = useState([]);
  const [blogContacts, setBlogContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [pendingRes, driversRes, shiftsRes, fleetRes, quotesRes, otpsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/admin/drivers/pending`, { headers }),
        axios.get(`${API}/admin/drivers`, { headers }),
        axios.get(`${API}/admin/shifts`, { headers }),
        axios.get(`${API}/fleet/registrations`),
        axios.get(`${API}/quotes/requests`),
        axios.get(`${API}/admin/otps`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/blog-contacts`, { headers }).catch(() => ({ data: [] }))
      ]);
      setPendingDrivers(pendingRes.data);
      setAllDrivers(driversRes.data);
      setAllShifts(shiftsRes.data);
      setFleetRegistrations(fleetRes.data);
      setQuoteRequests(quotesRes.data);
      setActiveOtps(otpsRes.data);
      setBlogContacts(contactsRes.data);
    } catch (error) { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  };

  const handleDriverAction = async (driverId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/drivers/${driverId}/approve`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Driver ${status}!`);
      fetchData();
    } catch { toast.error('Failed to update driver'); }
  };

  const handleLogout = () => { localStorage.removeItem('admin_token'); navigate('/admin'); };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-lg text-slate-600">Loading...</div></div>;

  const approvedDrivers = allDrivers.filter(d => d.status === 'approved');
  const activeShifts = allShifts.shifts?.filter(s => s.status === 'active') || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>PrimeSwift Admin</h1>
            <p className="text-xs text-slate-300">Unified Management Panel</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button data-testid="admin-jobs-button" variant="ghost" size="sm" onClick={() => navigate('/admin/jobs')} className="text-white hover:bg-slate-800"><Briefcase className="w-4 h-4 mr-2" />Jobs</Button>
            <Button data-testid="admin-blogs-button" variant="ghost" size="sm" onClick={() => navigate('/admin/blogs')} className="text-white hover:bg-slate-800"><BookOpen className="w-4 h-4 mr-2" />Blogs</Button>
            <Button data-testid="admin-users-button" variant="ghost" size="sm" onClick={() => navigate('/admin/users')} className="text-white hover:bg-slate-800"><Users className="w-4 h-4 mr-2" />Admins</Button>
            <Button data-testid="admin-logout-button" variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-slate-800"><LogOut className="w-4 h-4 mr-2" />Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          <Card className="border-slate-100 shadow-sm"><CardContent className="pt-6"><p className="text-sm text-slate-600">Pending Drivers</p><p className="text-3xl font-bold text-orange-500">{pendingDrivers.length}</p></CardContent></Card>
          <Card className="border-slate-100 shadow-sm"><CardContent className="pt-6"><p className="text-sm text-slate-600">Approved</p><p className="text-3xl font-bold text-emerald-600">{approvedDrivers.length}</p></CardContent></Card>
          <Card className="border-slate-100 shadow-sm"><CardContent className="pt-6"><p className="text-sm text-slate-600">Active Shifts</p><p className="text-3xl font-bold text-slate-900">{activeShifts.length}</p></CardContent></Card>
          <Card className="border-slate-100 shadow-sm"><CardContent className="pt-6"><p className="text-sm text-slate-600">Total Shifts</p><p className="text-3xl font-bold text-slate-900">{allShifts.shifts?.length || 0}</p></CardContent></Card>
          <Card className="border-slate-100 shadow-sm"><CardContent className="pt-6"><p className="text-sm text-slate-600">Fleet Leads</p><p className="text-3xl font-bold text-blue-600">{fleetRegistrations.length}</p></CardContent></Card>
          <Card className="border-slate-100 shadow-sm"><CardContent className="pt-6"><p className="text-sm text-slate-600">Quote Requests</p><p className="text-3xl font-bold text-blue-600">{quoteRequests.length}</p></CardContent></Card>
          <Card className="border-slate-100 shadow-sm"><CardContent className="pt-6"><p className="text-sm text-slate-600">Blog Contacts</p><p className="text-3xl font-bold text-green-600">{blogContacts.length}</p></CardContent></Card>
        </div>

        <Tabs defaultValue="pending" className="space-y-4">
          <TabsList className="bg-white border border-slate-200 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="pending" data-testid="pending-tab">Pending ({pendingDrivers.length})</TabsTrigger>
            <TabsTrigger value="otps" data-testid="otps-tab">OTPs ({activeOtps.length})</TabsTrigger>
            <TabsTrigger value="drivers" data-testid="drivers-tab">Drivers ({allDrivers.length})</TabsTrigger>
            <TabsTrigger value="shifts" data-testid="shifts-tab">Shifts ({allShifts.shifts?.length || 0})</TabsTrigger>
            <TabsTrigger value="fleet" data-testid="fleet-tab">Fleet Leads ({fleetRegistrations.length})</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="quotes-tab">Quotes ({quoteRequests.length})</TabsTrigger>
            <TabsTrigger value="blog-contacts" data-testid="blog-contacts-tab">Blog Contacts ({blogContacts.length})</TabsTrigger>
          </TabsList>

          {/* Pending Drivers */}
          <TabsContent value="pending">
            <Card className="border-slate-100 shadow-sm">
              <CardHeader><CardTitle>Pending Driver Approvals</CardTitle></CardHeader>
              <CardContent>
                {pendingDrivers.length === 0 ? <p className="text-center py-8 text-slate-600">No pending approvals</p> : (
                  <div className="space-y-4">
                    {pendingDrivers.map((driver) => (
                      <div key={driver.id} data-testid={`pending-driver-${driver.id}`} className="border border-slate-200 rounded-lg p-4">
                        <div className="space-y-2 mb-4">
                          <h3 className="font-semibold text-xl text-slate-900">{driver.name}</h3>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div><span className="text-slate-500">Mobile:</span> <span className="font-medium">{driver.mobile}</span></div>
                            {driver.email && <div><span className="text-slate-500">Email:</span> <span className="font-medium">{driver.email}</span></div>}
                            <div><span className="text-slate-500">License:</span> <span className="font-medium">{driver.license_number}</span></div>
                            {driver.vehicle_number && <div><span className="text-slate-500">Vehicle:</span> <span className="font-medium">{driver.vehicle_number}</span></div>}
                          </div>
                          {driver.address && <div className="text-sm"><span className="text-slate-500">Address:</span> {driver.address}, {driver.city}, {driver.state} - {driver.pincode}</div>}
                          {driver.bank_account_number && <div className="text-sm"><span className="text-slate-500">Bank:</span> {driver.bank_account_number} | IFSC: {driver.bank_ifsc}</div>}
                          {driver.emergency_contact_name && <div className="text-sm"><span className="text-slate-500">Emergency:</span> {driver.emergency_contact_name} - {driver.emergency_contact_number}</div>}
                          <p className="text-xs text-slate-400">Registered: {new Date(driver.created_at).toLocaleString()}</p>
                        </div>
                        <div className="flex space-x-2">
                          <Button data-testid={`approve-${driver.id}`} onClick={() => handleDriverAction(driver.id, 'approved')} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"><CheckCircle className="w-4 h-4 mr-2" />Approve</Button>
                          <Button data-testid={`reject-${driver.id}`} onClick={() => handleDriverAction(driver.id, 'rejected')} variant="destructive" className="flex-1 rounded-full"><XCircle className="w-4 h-4 mr-2" />Reject</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Active OTPs */}
          <TabsContent value="otps">
            <Card className="border-slate-100 shadow-sm">
              <CardHeader><CardTitle className="flex items-center space-x-2"><KeyRound className="w-5 h-5" /><span>Active OTPs</span></CardTitle></CardHeader>
              <CardContent>
                {activeOtps.length === 0 ? <p className="text-center py-8 text-slate-600">No active OTPs</p> : (
                  <div className="space-y-3">
                    {activeOtps.map((otp, i) => (
                      <div key={i} data-testid={`otp-${i}`} className="border border-slate-200 rounded-lg p-4 flex items-center justify-between">
                        <div className="space-y-1">
                          <h3 className="font-semibold">{otp.driver_name}</h3>
                          <p className="text-sm text-slate-600">Mobile: {otp.mobile}</p>
                          <p className="text-xs text-slate-400">Expires: {new Date(otp.expires_at).toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">OTP</p>
                          <p className="text-3xl font-bold text-emerald-600 tracking-widest">{otp.otp}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Drivers */}
          <TabsContent value="drivers">
            <Card className="border-slate-100 shadow-sm">
              <CardHeader><CardTitle>All Drivers</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {allDrivers.map((driver) => (
                    <div key={driver.id} data-testid={`driver-${driver.id}`} className="border border-slate-200 rounded-lg p-4 flex items-center justify-between">
                      <div className="space-y-1">
                        <h3 className="font-semibold">{driver.name}</h3>
                        <p className="text-sm text-slate-600">Mobile: {driver.mobile} | License: {driver.license_number}</p>
                      </div>
                      <Badge className={driver.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : driver.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}>{driver.status}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shifts */}
          <TabsContent value="shifts">
            <Card className="border-slate-100 shadow-sm">
              <CardHeader><CardTitle>All Shifts</CardTitle></CardHeader>
              <CardContent>
                {!allShifts.shifts?.length ? <p className="text-center py-8 text-slate-600">No shifts recorded</p> : (
                  <div className="space-y-6">
                    {allShifts.daily_summary?.length > 0 && (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                        <h3 className="font-semibold text-lg mb-3 flex items-center space-x-2"><Calendar className="w-5 h-5" /><span>Daily KM Summary</span></h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {allShifts.daily_summary.map((s, i) => (
                            <div key={i} data-testid={`daily-summary-${i}`} className="bg-white border border-slate-200 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs text-slate-500">{new Date(s.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                                <p className="text-2xl font-bold text-emerald-600">{s.total_km.toFixed(1)} <span className="text-xs text-slate-500">km</span></p>
                              </div>
                              <p className="text-sm font-medium text-slate-700">{s.driver_name}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="space-y-3">
                      {allShifts.shifts.map((shift) => (
                        <div key={shift.id} data-testid={`shift-${shift.id}`} className="border border-slate-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-3">
                              <h3 className="font-semibold">{shift.driver_name}</h3>
                              <Badge className={shift.status === 'active' ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'}>{shift.status}</Badge>
                            </div>
                            <div className="text-right">{shift.total_km ? <p className="text-xl font-bold text-emerald-600">{shift.total_km.toFixed(1)} km</p> : <p className="text-sm text-slate-400">In Progress</p>}</div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div><p className="text-slate-500">Date</p><p className="font-medium">{new Date(shift.start_time).toLocaleDateString()}</p></div>
                            <div><p className="text-slate-500">Start</p><p className="font-medium">{shift.start_km} km</p></div>
                            {shift.end_km && <div><p className="text-slate-500">End</p><p className="font-medium">{shift.end_km} km</p></div>}
                            <div><p className="text-slate-500">Time</p><p className="font-medium">{new Date(shift.start_time).toLocaleTimeString()}</p></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Fleet Leads (Website) */}
          <TabsContent value="fleet">
            <Card className="border-slate-100 shadow-sm">
              <CardHeader><CardTitle className="flex items-center space-x-2"><Truck className="w-5 h-5" /><span>Fleet Registration Leads</span></CardTitle></CardHeader>
              <CardContent>
                {fleetRegistrations.length === 0 ? <p className="text-center py-8 text-slate-600">No fleet registrations yet</p> : (
                  <div className="space-y-3">
                    {fleetRegistrations.map((reg, i) => (
                      <div key={reg.id || i} data-testid={`fleet-${i}`} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <h3 className="font-semibold text-lg">{reg.ownerName}</h3>
                            <div className="flex items-center space-x-4 text-sm text-slate-600">
                              <span className="flex items-center"><Phone className="w-3 h-3 mr-1" />{reg.phone}</span>
                              {reg.email && <span className="flex items-center"><Mail className="w-3 h-3 mr-1" />{reg.email}</span>}
                            </div>
                            <div className="text-sm text-slate-600">
                              {reg.vehicleType && <span>Vehicle: {reg.vehicleType} | </span>}
                              {reg.city && <span>City: {reg.city}</span>}
                            </div>
                            {reg.message && <p className="text-sm text-slate-500 mt-1">{reg.message}</p>}
                          </div>
                          <p className="text-xs text-slate-400">{reg.timestamp ? new Date(reg.timestamp).toLocaleDateString() : 'N/A'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quote Requests (Website) */}
          <TabsContent value="quotes">
            <Card className="border-slate-100 shadow-sm">
              <CardHeader><CardTitle className="flex items-center space-x-2"><FileText className="w-5 h-5" /><span>Quote Requests</span></CardTitle></CardHeader>
              <CardContent>
                {quoteRequests.length === 0 ? <p className="text-center py-8 text-slate-600">No quote requests yet</p> : (
                  <div className="space-y-3">
                    {quoteRequests.map((req, i) => (
                      <div key={req.id || i} data-testid={`quote-${i}`} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <h3 className="font-semibold text-lg">{req.companyName || req.contactPerson}</h3>
                            <div className="flex items-center space-x-4 text-sm text-slate-600">
                              {req.contactPerson && <span><strong>Contact:</strong> {req.contactPerson}</span>}
                              <span className="flex items-center"><Phone className="w-3 h-3 mr-1" />{req.phone}</span>
                            </div>
                            <div className="text-sm text-slate-600">
                              {req.serviceType && <span>Service: {req.serviceType} | </span>}
                              {req.origin && <span>Route: {req.origin} → {req.destination}</span>}
                            </div>
                            {req.details && <p className="text-sm text-slate-500 mt-1">{req.details}</p>}
                          </div>
                          <p className="text-xs text-slate-400">{req.timestamp ? new Date(req.timestamp).toLocaleDateString() : 'N/A'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blog Contacts */}
          <TabsContent value="blog-contacts">
            <Card className="border-slate-100 shadow-sm">
              <CardHeader><CardTitle className="flex items-center space-x-2"><MessageSquare className="w-5 h-5" /><span>Blog WhatsApp Contacts</span></CardTitle></CardHeader>
              <CardContent>
                {blogContacts.length === 0 ? <p className="text-center py-8 text-slate-600">No blog contacts yet</p> : (
                  <div className="space-y-3">
                    {blogContacts.map((c, i) => (
                      <div key={c.id || i} data-testid={`blog-contact-${i}`} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <h3 className="font-semibold text-lg">{c.name}</h3>
                            <div className="flex items-center space-x-4 text-sm text-slate-600">
                              <span className="flex items-center"><Phone className="w-3 h-3 mr-1" />{c.phone}</span>
                              <span>{c.city}</span>
                              <Badge className="bg-green-100 text-green-700">{c.type}</Badge>
                            </div>
                            {c.blog_title && <p className="text-sm text-slate-500">From blog: {c.blog_title}</p>}
                          </div>
                          <p className="text-xs text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboardNew;
